import * as Yup from 'yup';

import { AlertEnum, ButtonTypeEnum, LayoutEnum, SituacaoEleicaoEnum } from '../enums';
import { ApiHelper, CPFHelper } from '../helpers';
import { Button, CPFField, ComissaoEleitoralMembrosList, DefaultCard, DefaultContent } from '../components';
import { EleicaoResponseType, MembroComissaoEleicaoResponseType } from '../types';
import { Form, Formik } from 'formik';
import { FunctionComponent, useEffect, useState } from 'react';
import { useAlert, useAuthAxios, useErrorHandler, useErrorStatus, useLayout } from '../hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { Spinner } from '../utils';
import { StatusCodes } from 'http-status-codes';
import { isValid as isValidCpf } from '@fnando/cpf';
import { useTranslation } from 'react-i18next';

const MembroComissaoSchema = (t: Function) =>
  Yup.object().shape({
    identificador: Yup.string()
      .required(t('validacao.campo_obrigatorio'))
      .test('isCPF', t('validacao.cpf_invalido'), (value: any) => {
        return isValidCpf(CPFHelper.pad(value), true);
      }),
  });

const initialValues = {
  identificador: '',
};

const ComissaoEleitoralGerenciamentoView: FunctionComponent = () => {
  const { t } = useTranslation();
  const axios = useAuthAxios();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();
  const [eleicao, setEleicao] = useState<EleicaoResponseType>();
  const [loadingEleicao, setLoadingEleicao] = useState(true);
  const [membrosComissao, setMembrosComissao] = useState<MembroComissaoEleicaoResponseType[]>([]);
  const [loadingComissao, setLoadingComissao] = useState(true);
  let { idEleicao } = useParams();
  const { createAlert } = useAlert();
  const { setErrorStatus } = useErrorStatus();
  const { setLayout } = useLayout();

  useEffect(() => {
    setLayout(LayoutEnum.BASE_LAYOUT);
  }, []);

  useEffect(() => {
    if (idEleicao) {
      if (ApiHelper.isValidIntegerToApi(idEleicao)) {
        axios
          .get(ApiHelper.getUrl.apiSCE.eleicao(+idEleicao))
          .then((response) => {
            const dadosEleicao: EleicaoResponseType = response.data;
            if (dadosEleicao.situacao !== SituacaoEleicaoEnum.NAO_PUBLICADA.key) {
              navigate(`/eleicoes/${idEleicao}`);
            }
            setEleicao(dadosEleicao);
            axios
              .get(ApiHelper.getUrl.apiSCE.membrosComissao(+idEleicao!))
              .then((response) => setMembrosComissao(response.data))
              .catch((error) => errorHandler(error, t('erro.recuperar_comissao')))
              .finally(() => setLoadingComissao(false));
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_eleicao'));
            }
          })
          .finally(() => setLoadingEleicao(false));
      } else {
        setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
      }
    }
  }, [idEleicao]);

  return loadingEleicao ? (
    <Spinner />
  ) : (
    <DefaultContent
      subtitle={eleicao?.titulo!}
      title={t('label.gerenciar_comissao_eleitoral')}
      pageAction={<Button title={t('label.voltar')} onClick={() => navigate(`/eleicoes/${eleicao?.id!}`)} outline />}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={MembroComissaoSchema(t)}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setTimeout(() => {
            axios(ApiHelper.getUrl.apiSCE.membrosComissao(eleicao?.id!), {
              data: values,
              method: 'POST',
            })
              .then((response) => {
                createAlert(t('sucesso.adicionar_membro_comissao'), AlertEnum.SUCCESS);
                setMembrosComissao([...membrosComissao, response.data]);
                resetForm();
              })
              .catch((error) => errorHandler(error, t('erro.adicionar_membro_comissao')))
              .finally(() => {
                setSubmitting(false);
              });
          }, 400);
        }}
      >
        {(props) => (
          <Form>
            <DefaultCard
              cardTitle={t('label.adicionar_membro_comissao')}
              helpLink={
                'https://conhecimento.sti.ufpb.br/books/sistema-de-cadastro-de-eleicoes/page/gerenciar-os-membros-da-comissao-eleitoral'
              }
              footer={
                <div className="d-flex justify-content-center gap-5">
                  <Button
                    title={t('label.adicionar')}
                    type={ButtonTypeEnum.SUBMIT}
                    disabled={props.isSubmitting || props.isValidating}
                  />
                </div>
              }
            >
              <div className="mb-3">
                <CPFField formikProps={props} name="identificador" label={t('label.cpf')} required />
              </div>
            </DefaultCard>
          </Form>
        )}
      </Formik>
      <DefaultCard cardTitle={t('label.comissao_eleitoral')}>
        {loadingComissao ? (
          <Spinner />
        ) : (
          <ComissaoEleitoralMembrosList
            membrosComissao={membrosComissao!}
            idEleicao={eleicao?.id!}
            setMembrosComissao={setMembrosComissao}
            alteracao={true}
          />
        )}
      </DefaultCard>
    </DefaultContent>
  );
};

export default ComissaoEleitoralGerenciamentoView;
